/**
 * The application's last line of defence: anything that throws during render
 * shows a message and a reload control instead of unmounting the tree.
 *
 * THIS APP HAD NONE (found 2026-08-20). Every view is a lazy chunk; when a
 * stale client's chunk import 404'd after a deploy, React unmounted everything
 * and users saw a BLANK SCREEN — reported as wallet setup "not working at all"
 * and manual create "shows an empty screen". `lazyWithReload` now heals that
 * specific failure with one reload; this boundary is what stands behind it for
 * the second failure, and for every other render-time crash that used to blank
 * the app.
 *
 * DELIBERATELY DEPENDENCY-FREE. A boundary renders precisely when the app's
 * own machinery cannot be trusted, so it uses no i18n, no store, no router —
 * hard-coded English and a full reload are the point, not a shortcut. (The
 * Suspense fallback's English "Loading" label is the same judgement.)
 *
 * A class component because error boundaries still have no hook equivalent:
 * `getDerivedStateFromError` exists only on classes.
 */

import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  failed: boolean;
}

export default class AppErrorBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: unknown): void {
    // The ONLY signal an operator gets that clients are crashing at render.
    console.error('app render crashed', error);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div
        role="alert"
        style={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--space-s, 1rem)',
          padding: 'var(--space-l, 2rem)',
          textAlign: 'center',
        }}
      >
        <p style={{ margin: 0, color: 'var(--color-text-muted, inherit)' }}>
          Something went wrong. Your wallets and funds are not affected.
        </p>
        <button
          type="button"
          data-variant="submit"
          style={{ maxWidth: '20rem' }}
          onClick={() => window.location.reload()}
        >
          Reload
        </button>
      </div>
    );
  }
}
